import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateReclassEntry,
  ReclassEntryStatus,
  ReclassificationEntry,
  ReclassificationEntryListRow,
  ReverseReclassNextPeriod,
  ReverseReclassResult,
  UpdateReclassEntry,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache-key scope — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (matches prepaid / ar / accrual conventions).
const entriesKey = (companyId: string | undefined) => ['reclass-entries', companyId] as const;

export interface ReclassFilters {
  period?: string;
  category?: string;
  status?: ReclassEntryStatus;
}

export function useReclassEntries(companyId: string | undefined, filters: ReclassFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.category) qs.set('category', filters.category);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...entriesKey(companyId), filters],
    queryFn: () =>
      api<ReclassificationEntryListRow[]>(
        `/companies/${companyId}/reclass-entries${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useReclassEntry(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['reclass-entries', companyId, id],
    queryFn: () =>
      api<ReclassificationEntryListRow>(`/companies/${companyId}/reclass-entries/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateReclassEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateReclassEntry) =>
      api<ReclassificationEntry>(`/companies/${companyId}/reclass-entries`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
    },
  });
}

export function useUpdateReclassEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReclassEntry }) =>
      api<ReclassificationEntry>(`/companies/${companyId}/reclass-entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
    },
  });
}

export function useDeleteReclassEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; deleted: true }>(`/companies/${companyId}/reclass-entries/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
    },
  });
}

export function usePostReclassEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, idempotencyKey }: { id: string; idempotencyKey?: string }) =>
      api<ReclassificationEntry>(`/companies/${companyId}/reclass-entries/${id}/post`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

export function useReverseReclassNextPeriod(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: ReverseReclassNextPeriod;
      idempotencyKey?: string;
    }) =>
      api<ReverseReclassResult>(`/companies/${companyId}/reclass-entries/reverse-next-period`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: entriesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}
